import React, { memo, useMemo, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Sphere,
  Graticule,
  Annotation
} from "react-simple-maps";
import { useNavigate } from "react-router-dom";
import { Table } from "antd";

const geoUrl = "/features.json";

// Colors
const oceanColor  = "#f0f9ff";
const landColor   = "#93c5fd";
const gridColor   = "#CCCCCC";

const labelOffsets = {
  // North America
  FOMC:   { dx:  -50, dy: 20 },
  BoC:    { dx:  -50, dy: 5 },
  BdeM:   { dx:  50, dy:   5 },

  // Latin America
  BanRep: { dx:  50, dy:  -10 },
  BCRP:   { dx:  10, dy:   -20 },
  BCB:    { dx:  0, dy:  20 },
  CBoC:   { dx:  -20, dy:   20 },

  // Europe
  BoE:    { dx: -40, dy: -10 },
  ECB:    { dx: -50, dy:   20 },
  SNB:    { dx: -45, dy:  40 },
  NBP:    { dx:  0, dy: -20 },
  CBR:    { dx:  40, dy: 0 },
  CBRT:   { dx:  50, dy:  5 },
  CBE:    { dx:  -40, dy:  20 },
  BoI:    { dx:  20, dy:  20 },

  // South & SE Asia
  PBoC:   { dx: 30, dy:   -40 },
  BoK:    { dx:  30, dy: -20 },
  BoJ:    { dx:  45, dy:   0 },
  BSP:    { dx:  60, dy:  20 },
  BoT:    { dx:  20, dy:  20 },
  CBCT:   { dx:  20, dy:  20 },
  RBI:    { dx:  10, dy: -20 },

  // ASEAN & Oceania
  MAS:    { dx: 20, dy: 40 },
  BNM:    { dx: 60, dy:  20 },
  RBA:    { dx: 20, dy:  20 },
};

const datasetStats = [
  { category: "Central Banks", value: "25" },
  { category: "Years", value: "1996–2024" },
  { category: "Scraped Sentences", value: "380,200" },
  { category: "Annotated Sentences", value: "25,000" },
  { category: "Total Words", value: "10,289,163" },
  { category: "Corpus Size", value: "2,661,400" },
  { category: "Sentences/Year*", value: "13,110.34" },
  { category: "Words/Sentence*", value: "27.06" }
];

// Updated modelStats with hyperlinks
const modelStats = [
  { category: "Pretrained Language Models", value: "7" },
  { category: "Large Language Models", value: "9" },
  {
    category: "Best Stance Model*",
    value: (
      <a
        href="https://huggingface.co/gtfintechlab/model_WCB_stance_label"
        target="_blank"
        rel="noopener noreferrer"
        className='text-blue-500 underline hover:text-blue-500 hover:underline'
      >
        RoBERTa-Large (0.740)
      </a>
    )
  },
  {
    category: "Best Temporal Model*",
    value: (
      <a
        href="https://huggingface.co/gtfintechlab/model_WCB_time_label"
        target="_blank"
        rel="noopener noreferrer"
        className='text-blue-500 underline hover:text-blue-500 hover:underline'
      >
        RoBERTa-Base (0.868)
      </a>
    )
  },
  {
    category: "Best Uncertainty Model*",
    value: (
      <a
        href="https://huggingface.co/gtfintechlab/model_WCB_certain_label"
        target="_blank"
        rel="noopener noreferrer"
        className='text-blue-500 underline hover:text-blue-500 hover:underline'
      >
        RoBERTa-Large (0.846)
      </a>
    )
  },
  { category: "Benchmarking Experiments", value: "15,075" },
  { category: "Few Shot", value: "✓" },
  { category: "Few Shot + Ann. Guide", value: "✓" }
];


const annotationStats = [
  { category: "Annotators", value: "104" },
  { category: "Annotation Guides", value: "26" },
  { category: "Annotation Steps", value: "6" },
  { category: "Tasks", value: "" },
  { category: "Stance Detection", value: "Hawkish, Dovish, Neutral, Irrelevant" },
  { category: "Temporal Classification", value: "(Not) Forward Looking" },
  { category: "Uncertainty Estimation", value: "(Un)certain" }
];
// Updated getColumns to center header and cell text
const getColumns = (title, bgColor) => [
  {
    title: <strong>{title}</strong>,
    dataIndex: "category",
    key: "category",
    width: "60%",
    align: "center",
    onHeaderCell: () => ({
      style: {
        backgroundColor: bgColor,
        textAlign: "center",
        padding: "8px"
      }
    }),
    render: (text) => (
      <span style={{ fontWeight: text === "Tasks" ? "bold" : undefined }}>
        {text}
      </span>
    )
  },
  {
    title: "",
    dataIndex: "value",
    key: "value",
    align: "center",
    onHeaderCell: () => ({
      style: {
        backgroundColor: bgColor,
        padding: "8px"
      }
    }),
    render: (text) => <span>{text}</span>
  }
];

const MapChart = ({ centralBankPins }) => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);
  return (
    <div style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.05)", borderRadius: 12, overflow: "visible" }}>
      <ComposableMap
        projectionConfig={{ rotate: [-10, 0, 0], scale: 147 }}
        style={{ width: "100%", height: "auto", cursor: "pointer" }}
      >
        {/* ocean & grid */}
        <Sphere fill={oceanColor} stroke="#0000000D" strokeWidth={0.5} />
        <Graticule stroke={gridColor} strokeWidth={0.5} />

        {/* land */}
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map(geo => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill={landColor}
                style={{ default: { outline: "none" } }}
              />
            ))
          }
        </Geographies>

        {centralBankPins.map(({ name, lat, lng }) => (
          <Marker key={`dot-${name}`} coordinates={[lng, lat]}>
            <circle r={2} fill="#000" />
          </Marker>
        ))}

        {centralBankPins.map(({ name, lat, lng }) => {
  const { dx, dy } = labelOffsets[name] || { dx: 20, dy: -20 };
  const isHover = hovered === name;
  const LABEL_HEIGHT = 24;
  const LABEL_WIDTH  = name.length * 8 + 40;
  return (
    <Annotation
      key={name}
      subject={[lng, lat]}
      dx={dx}
      dy={dy}
      connectorProps={{
        stroke: "#333",
        strokeWidth: 1,
        strokeLinecap: "round"
      }}
      onMouseEnter={() => setHovered(name)}
      onMouseLeave={() => setHovered(null)}
      onClick={() => navigate(`/${name.toLowerCase()}`)}
    >
      <foreignObject
        x={-LABEL_WIDTH  / 2}
        y={-LABEL_HEIGHT / 2}
        width={LABEL_WIDTH}
        height={LABEL_HEIGHT}
      >
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          className={`annotation-label ${hovered === name ? " hover" : ""}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "2px 6px",
            backgroundColor: isHover ? "#eee" : "#fff",
            border: "1px solid #333",
            borderRadius: "4px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
            transition: "background-color 0.2s ease"
          }}
        >
          <img
            src={`/static/images/flags/${name}.png`}
            alt={name}
            style={{
              width: "auto",
              height: "12px",
              border: "1px solid #000",
              borderRadius: "2px",
              flexShrink: 0
            }}
          />
          <span
            style={{
              marginLeft: "4px",
              fontSize: "8px",
              color: "#111",
              fontWeight: 600,
              lineHeight: 1
            }}
          >
            {name}
          </span>
        </div>
      </foreignObject>
    </Annotation>
  );
})}

      </ComposableMap>
      <div className="mar_tit">
      <p className="text-align mr-16 text-gray-700">*Interactive Map showing various Central Banks is hoverable.</p>
      <div className="stats-container">
        <div style={{ flex: 1, minWidth: 280 }}>
<div className="no-subheader" style={{ flex: 1, minWidth: 280 }}>
  <Table
    dataSource={datasetStats}
    pagination={false}
    size="small"
    bordered
    columns={[
      {
        title: <strong>Dataset</strong>,
        onHeaderCell: () => ({
          style: {
            backgroundColor: "#cce5ff",
            textAlign: "center",
            padding: "8px"
          }
        }),
        children: [
          {
            dataIndex: "category",
            key: "category",
            align: "center",
            render: text => <span>{text}</span>
          },
          {
            dataIndex: "value",
            key: "value",
            align: "center",
            render: text => <span>{text}</span>
          }
        ]
      }
    ]}
  />
</div>
        </div>

        {/* Models */}
        <div style={{ flex: 1, minWidth: 280 }}>
{/* Models table */}
<div className="no-subheader" style={{ flex: 1, minWidth: 280 }}>
  <Table
    dataSource={modelStats}
    pagination={false}
    size="small"
    bordered
    columns={[
      {
        title: <strong>Models</strong>,
        onHeaderCell: () => ({
          style: {
            backgroundColor: "#fff9c4",
            textAlign: "center",
            padding: "8px"
          }
        }),
        children: [
          {
            dataIndex: "category",
            key: "category",
            align: "center",
            render: text => <span>{text}</span>
          },
          {
            dataIndex: "value",
            key: "value",
            align: "center",
            render: text => <span>{text}</span>
          }
        ]
      }
    ]}
  />
</div>


        </div>

        <div style={{ flex: 1, minWidth: 320 }}>
<div className="no-subheader" style={{ flex: 1, minWidth: 320 }}>
  <Table
    dataSource={annotationStats}
    pagination={false}
    size="small"
    bordered
    columns={[
      {
        title: <strong>Annotations</strong>,
        onHeaderCell: () => ({
          style: {
            backgroundColor: "#d4edda",
            textAlign: "center",
            padding: "8px"
          }
        }),
        children: [
          {
            dataIndex: "category",
            key: "category",
            align: "center",
            render: text => (
              <span style={{ fontWeight: text === "Tasks" ? "bold" : undefined }}>
                {text}
              </span>
            )
          },
          {
            dataIndex: "value",
            key: "value",
            align: "center",
            render: text => <span>{text}</span>
          }
        ]
      }
    ]}
    onRow={record =>
      record.category === "Tasks"
        ? { style: { backgroundColor: "#d4edda", fontWeight: "bold" } }
        : {}
    }
  />
</div>


        </div>
      </div>
      </div>
    </div>
  );
};

export default memo(MapChart);
